import React from 'react';

import styled from '@emotion/styled';
import { Box, FlameTheme, flameTheme, themeGet } from '@lightspeed/flame/Core';
import { Popover, PopoverProps } from '@lightspeed/flame/Popover';

interface Props extends Omit<PopoverProps, 'target'> {
  content?: React.ReactNode;
  title?: React.ReactNode;
  button?: React.ReactNode;
  widthStyle?: 'fixed' | 'max' | 'none';
  width?: string;
  style?: React.CSSProperties;
  icon?: React.ReactNode;
}

const TooltipContent = styled(Box)`
  color: ${themeGet('tooltipStyles.dark.color')};
  font-size: 12px;
  font-weight: normal;
  line-height: 1rem;
  padding: 12px;
  text-align: left;
  text-transform: none;
  text-wrap: initial;
`;

const TooltipTitle = styled('h2')`
  color: ${themeGet('tooltipStyles.dark.color')};
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
`;

const TooltipAction = styled(Box)`
  display: flex;
  justify-content: right;
  margin-top: 20px;
`;

// fix bug where the tooltip is not positioned correctly when using start or end placements
const className = 'tooltip-interactive-help-popper';
const offsetFix = '-0.7em';
const TooltipHelpContainer = styled('span')`
  .${className} {
    &[data-placement='left-start'],
    &[data-placement='right-start'] {
      margin-top: ${offsetFix};
    }
    &[data-placement='left-end'],
    &[data-placement='right-end'] {
      margin-bottom: ${offsetFix};
    }
    &[data-placement='top-start'],
    &[data-placement='bottom-start'] {
      margin-left: ${offsetFix};
    }
    &[data-placement='top-end'],
    &[data-placement='bottom-end'] {
      margin-right: ${offsetFix};
    }
  }
`;

const TooltipHelp: React.FC<Props> = ({
  content,
  title,
  button,
  style: _style,
  widthStyle = 'fixed',
  width = '300px',
  icon: _icon,
  ...otherProps
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  if (!content) {
    return null;
  }
  const icon: React.ReactNode = _icon ?? (
    <svg className="cr-icon cr-icon-help cr-icon-help-inverted" data-test="tooltip-help-icon">
      <use xlinkHref="#cr-icon-help" />
    </svg>
  );

  const style: React.CSSProperties = {};
  if (widthStyle === 'fixed') {
    style.width = width;
  } else if (widthStyle === 'max') {
    style.maxWidth = width;
  }
  if (style) Object.assign(style, _style);

  let delayTimout: NodeJS.Timeout | null = null;
  const handleMouseEnter = () => {
    if (delayTimout) clearTimeout(delayTimout);
    setIsOpen(true);
  };
  const handleMouseLeave = () => {
    if (delayTimout) clearTimeout(delayTimout);
    delayTimout = setTimeout(() => setIsOpen(false), 200);
  };

  return (
    <FlameTheme
      // fixing bug where popover is using gray-50 (white-ish) as its background color
      themeOverrides={{
        popoverStyles: {
          dark: { ...flameTheme.tooltipStyles.dark },
        },
      }}
    >
      <TooltipHelpContainer
        onMouseOver={handleMouseEnter}
        onFocus={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onBlur={handleMouseLeave}
      >
        <Popover
          placement="top"
          isOpen={isOpen}
          className={className}
          zIndex={1}
          target={({ targetProps, targetEvents }) => {
            return (
              <span {...targetProps} {...targetEvents} data-test="tooltip-help-icon-container">
                {icon}
              </span>
            );
          }}
          {...otherProps}
        >
          <TooltipContent style={style} data-test="tooltip-help-content">
            {title ? <TooltipTitle>{title}</TooltipTitle> : null}
            {content}
            {button ? <TooltipAction>{button}</TooltipAction> : null}
          </TooltipContent>
        </Popover>
      </TooltipHelpContainer>
    </FlameTheme>
  );
};

export default TooltipHelp;
